// The app's single API client: an authed axios instance (in-memory access token,
// httpOnly refresh cookie, automatic refresh + server-wake retry) plus one exported
// namespace per backend domain (auth, admin, proposals, opportunities, kb, sam,
// forum, ...) mirroring the route modules in backend/routes/.
import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const USER_KEY = "govcon_user";
const ACTIVE_PROPOSAL_KEY = "govcon_active_proposal";

// Access token is kept in memory only (the refresh token lives in an httpOnly cookie).
let accessToken = null;
export const getToken = () => accessToken;
export const setToken = (t) => {
  accessToken = t || null;
};
export const clearToken = () => {
  accessToken = null;
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(ACTIVE_PROPOSAL_KEY);
};

export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
};
export const setStoredUser = (u) => localStorage.setItem(USER_KEY, JSON.stringify(u));

// The proposal currently open in Workspace/Compliance/Submission, so switching
// proposals in one keeps the other two in sync even when navigated to via a
// plain sidebar link (no ?proposal= in the URL).
export const getActiveProposalId = () => localStorage.getItem(ACTIVE_PROPOSAL_KEY) || null;
export const setActiveProposalId = (id) => {
  if (id) localStorage.setItem(ACTIVE_PROPOSAL_KEY, id);
};

// withCredentials so the httpOnly refresh cookie is sent on /auth/refresh & /auth/logout.
// timeout is generous: the backend (Render free tier) can take ~30-60s to wake from idle.
export const api = axios.create({ baseURL: API, withCredentials: true, timeout: 75000 });

// True while a request is being retried because the server appears to be waking up.
// The auth screens subscribe to surface a "server is starting…" hint instead of an error.
let _waking = false;
const _wakeSubs = new Set();
export const onServerWaking = (fn) => { _wakeSubs.add(fn); return () => _wakeSubs.delete(fn); };
const setWaking = (v) => { _waking = v; _wakeSubs.forEach((f) => { try { f(v); } catch { /* noop */ } }); };
export const isServerWaking = () => _waking;

api.interceptors.request.use((cfg) => {
  if (accessToken) cfg.headers.Authorization = `Bearer ${accessToken}`;
  return cfg;
});

// Cold-start resilience: a sleeping free-tier backend returns no response (network
// error) or a gateway 502/503/504 with no CORS headers for the first request(s).
// Retry those transparently with backoff so a wake-up looks like a slow load, not a failure.
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const _isWakeError = (err) => {
  if (err?.code === "ERR_CANCELED") return false;
  if (!err?.response) return true; // network error / timeout / blocked (cold start)
  return [502, 503, 504].includes(err.response.status);
};
api.interceptors.response.use(
  (r) => { if (_waking) setWaking(false); return r; },
  async (err) => {
    const cfg = err?.config || {};
    if (_isWakeError(err) && (cfg.__wakeRetries || 0) < 4) {
      cfg.__wakeRetries = (cfg.__wakeRetries || 0) + 1;
      setWaking(true);
      await _sleep(Math.min(2000 * cfg.__wakeRetries, 6000));
      return api(cfg);
    }
    if (_waking) setWaking(false);
    return Promise.reject(err);
  }
);

// On 401, try a single silent refresh, then replay the original request.
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config || {};
    const url = cfg.url || "";
    const isAuthCall = url.includes("/auth/refresh") || url.includes("/auth/login");
    if (err?.response?.status === 401 && !cfg.__retried && !isAuthCall) {
      cfg.__retried = true;
      try {
        if (!refreshing) refreshing = api.post("/auth/refresh").finally(() => { refreshing = null; });
        const { data } = await refreshing;
        setToken(data.token);
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${data.token}`;
        return api(cfg);
      } catch {
        clearToken();
        if (!window.location.pathname.startsWith("/login")) window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const auth = {
  login: (email, password, remember = false) =>
    api.post("/auth/login", { email, password, remember }).then((r) => r.data),
  register: (data) => api.post("/auth/register", data).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  refresh: () => api.post("/auth/refresh").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  verifyEmail: (token) => api.post("/auth/verify-email", { token }).then((r) => r.data),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, new_password) =>
    api.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  changePassword: (current_password, new_password) =>
    api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  updateProfile: (data) => api.patch("/auth/me", data).then((r) => r.data),
};

export const admin = {
  roles: () => api.get("/admin/roles").then((r) => r.data),
  users: () => api.get("/admin/users").then((r) => r.data),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data).then((r) => r.data),
  overview: () => api.get("/admin/overview").then((r) => r.data),
  audit: (limit = 200) => api.get(`/admin/audit?limit=${limit}`).then((r) => r.data),
  createUser: (data) => api.post("/admin/users", data).then((r) => r.data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),
  pendingUsers: () => api.get("/admin/pending-users").then((r) => r.data),
  approveUser: (id) => api.post(`/admin/users/${id}/approve`).then((r) => r.data),
  rejectUser: (id) => api.post(`/admin/users/${id}/reject`).then((r) => r.data),
  proposals: () => api.get("/admin/proposals").then((r) => r.data),
  updateProposal: (id, data) => api.patch(`/admin/proposals/${id}`, data).then((r) => r.data),
  deleteProposal: (id) => api.delete(`/admin/proposals/${id}`).then((r) => r.data),
  deleteOpportunity: (id) => api.delete(`/admin/opportunities/${id}`).then((r) => r.data),
};

// Super-admin (cross-organization) operations.
export const superadmin = {
  tenants: () => api.get("/superadmin/tenants").then((r) => r.data),
  createTenant: (name) => api.post("/superadmin/tenants", { name }).then((r) => r.data),
  deleteTenant: (id) => api.delete(`/superadmin/tenants/${id}`).then((r) => r.data),
  tenantUsers: (id) => api.get(`/superadmin/tenants/${id}/users`).then((r) => r.data),
  createUserInTenant: (id, data) => api.post(`/superadmin/tenants/${id}/users`, data).then((r) => r.data),
  moveUser: (userId, data) => api.patch(`/superadmin/users/${userId}`, data).then((r) => r.data),
  leads: () => api.get("/superadmin/leads").then((r) => r.data),
};

// Community Forum — platform-wide (cross-org), not tenant-scoped.
export const forum = {
  categories: () => api.get("/forum/categories").then((r) => r.data),
  threads: (params = {}) => api.get("/forum/threads", { params }).then((r) => r.data),
  thread: (id) => api.get(`/forum/threads/${id}`).then((r) => r.data),
  createThread: (data) => api.post("/forum/threads", data).then((r) => r.data),
  updateThread: (id, data) => api.patch(`/forum/threads/${id}`, data).then((r) => r.data),
  deleteThread: (id) => api.delete(`/forum/threads/${id}`).then((r) => r.data),
  reply: (id, body) => api.post(`/forum/threads/${id}/posts`, { body }).then((r) => r.data),
  updatePost: (id, body) => api.patch(`/forum/posts/${id}`, { body }).then((r) => r.data),
  deletePost: (id) => api.delete(`/forum/posts/${id}`).then((r) => r.data),
};

// Download a file through the authed axios instance, then trigger a browser save.
export const downloadBlob = async (path, filename, params = {}) => {
  const res = await api.get(path, { responseType: "blob", params });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const dashboard = {
  stats: () => api.get("/dashboard/stats").then((r) => r.data),
};

export const leads = {
  create: (email, source = "landing") => api.post("/leads", { email, source }).then((r) => r.data),
};

export const opportunities = {
  list: () => api.get("/opportunities").then((r) => r.data),
  upload: (file, passwords = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("passwords", JSON.stringify(passwords));
    return api
      .post("/opportunities/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  shred: (id, force = false) => api.post(`/opportunities/${id}/shred`, { force }).then((r) => r.data),
  respond: (id) => api.post(`/opportunities/${id}/respond`).then((r) => r.data),
};

export const ingestion = {
  job: (id) => api.get(`/ingestion/jobs/${id}`).then((r) => r.data),
};

export const exporter = {
  matrixXlsx: (id) => downloadBlob(`/proposals/${id}/export/matrix.xlsx`, `compliance_matrix_${id.slice(0, 8)}.xlsx`),
  docx: (id, opts = {}) => downloadBlob(`/proposals/${id}/export/proposal.docx`, `proposal_${id.slice(0, 8)}.docx`, opts),
  pdf: (id, opts = {}) => downloadBlob(`/proposals/${id}/export/proposal.pdf`, `proposal_${id.slice(0, 8)}.pdf`, opts),
  packageZip: (id, opts = {}) => downloadBlob(`/proposals/${id}/export/package.zip`, `submission_${id.slice(0, 8)}.zip`, opts),
  auditCsv: (id) => downloadBlob(`/proposals/${id}/audit.csv`, `audit_${id.slice(0, 8)}.csv`),
};

export const sam = {
  status: () => api.get("/sam/status").then((r) => r.data),
  sync: (filters = {}) => api.post("/sam/sync", filters).then((r) => r.data),
  history: () => api.get("/sam/sync-history").then((r) => r.data),
};

export const proposals = {
  list: () => api.get("/proposals").then((r) => r.data),
  get: (id) => api.get(`/proposals/${id}`).then((r) => r.data),
  create: (data) => api.post("/proposals", data).then((r) => r.data),
  uploadPreview: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/proposals/upload-preview", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  remove: (id) => api.delete(`/proposals/${id}`).then((r) => r.data),
  archive: (id) => api.post(`/proposals/${id}/archive`).then((r) => r.data),
  restore: (id) => api.post(`/proposals/${id}/restore`).then((r) => r.data),
  addDocuments: (id, files, passwords = {}) => {
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    fd.append("passwords", JSON.stringify(passwords));
    return api
      .post(`/proposals/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  uploadAmendment: (id, files, passwords = {}) => {
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    fd.append("passwords", JSON.stringify(passwords));
    return api
      .post(`/proposals/${id}/amendments`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  updateSection: (id, sectionId, body) => api.patch(`/proposals/${id}/sections/${sectionId}`, body).then((r) => r.data),
  updateRequirement: (id, reqId, body) => api.patch(`/proposals/${id}/requirements/${reqId}`, body).then((r) => r.data),
  generate: (id, sectionId, requirementIds) =>
    api.post(`/proposals/${id}/generate`, { section_id: sectionId, requirement_ids: requirementIds }).then((r) => r.data),
  assist: (id, text, action) => api.post(`/proposals/${id}/assist`, { text, action }).then((r) => r.data),
  checklist: (id) => api.get(`/proposals/${id}/checklist`).then((r) => r.data),
  preflight: (id) => api.get(`/proposals/${id}/export/preflight`).then((r) => r.data),
  lockSection: (id, sectionId) => api.post(`/proposals/${id}/sections/${sectionId}/lock`).then((r) => r.data),
  unlockSection: (id, sectionId) => api.post(`/proposals/${id}/sections/${sectionId}/unlock`).then((r) => r.data),
  versions: (id) => api.get(`/proposals/${id}/versions`).then((r) => r.data),
  createVersion: (id) => api.post(`/proposals/${id}/versions`).then((r) => r.data),
  getVersion: (id, vid) => api.get(`/proposals/${id}/versions/${vid}`).then((r) => r.data),
  restoreVersion: (id, vid) => api.post(`/proposals/${id}/versions/${vid}/restore`).then((r) => r.data),
  snapshots: (id) => api.get(`/proposals/${id}/snapshots`).then((r) => r.data),
  createSnapshot: (id, label) => api.post(`/proposals/${id}/snapshots`, { label }).then((r) => r.data),
  snapshotPdf: (id, sid) => downloadBlob(`/proposals/${id}/snapshots/${sid}.pdf`, `snapshot_${sid.slice(0, 8)}.pdf`),
  comments: (id) => api.get(`/proposals/${id}/comments`).then((r) => r.data),
  addComment: (id, body) => api.post(`/proposals/${id}/comments`, body).then((r) => r.data),
  resolveComment: (id, cid, resolved = true) => api.patch(`/proposals/${id}/comments/${cid}`, { resolved }).then((r) => r.data),
  audit: (id) => api.get(`/proposals/${id}/audit`).then((r) => r.data),
  addMember: (id, user_id, role) => api.post(`/proposals/${id}/members`, { user_id, role }).then((r) => r.data),
  removeMember: (id, user_id) => api.delete(`/proposals/${id}/members/${user_id}`).then((r) => r.data),
  exportMatrix: (id) => `${API}/proposals/${id}/export/matrix.xlsx`,
  exportPackage: (id) => `${API}/proposals/${id}/export/package.zip`,
  getSkillVerification: (id) => api.get(`/proposals/${id}/skill-verification`).then((r) => r.data),
  generateSkillVerification: (id) => api.post(`/proposals/${id}/skill-verification`).then((r) => r.data),
  answerSkillVerification: (id, answers) =>
    api.patch(`/proposals/${id}/skill-verification`, { answers }).then((r) => r.data),
};

export const kb = {
  pastPerformance: () => api.get("/kb/past-performance").then((r) => r.data),
  addPastPerformance: (data) => api.post("/kb/past-performance", data).then((r) => r.data),
  updatePastPerformance: (id, data) => api.patch(`/kb/past-performance/${id}`, data).then((r) => r.data),
  delPastPerformance: (id) => api.delete(`/kb/past-performance/${id}`).then((r) => r.data),
  skills: () => api.get("/kb/skills").then((r) => r.data),
  addSkill: (data) => api.post("/kb/skills", data).then((r) => r.data),
  updateSkill: (id, data) => api.patch(`/kb/skills/${id}`, data).then((r) => r.data),
  delSkill: (id) => api.delete(`/kb/skills/${id}`).then((r) => r.data),
  certifications: () => api.get("/kb/certifications").then((r) => r.data),
  addCert: (data) => api.post("/kb/certifications", data).then((r) => r.data),
  updateCert: (id, data) => api.patch(`/kb/certifications/${id}`, data).then((r) => r.data),
  delCert: (id) => api.delete(`/kb/certifications/${id}`).then((r) => r.data),
  naics: () => api.get("/kb/naics").then((r) => r.data),
  addNaics: (data) => api.post("/kb/naics", data).then((r) => r.data),
  updateNaics: (id, data) => api.patch(`/kb/naics/${id}`, data).then((r) => r.data),
  delNaics: (id) => api.delete(`/kb/naics/${id}`).then((r) => r.data),
  clearances: () => api.get("/kb/clearances").then((r) => r.data),
  addClearance: (data) => api.post("/kb/clearances", data).then((r) => r.data),
  updateClearance: (id, data) => api.patch(`/kb/clearances/${id}`, data).then((r) => r.data),
  delClearance: (id) => api.delete(`/kb/clearances/${id}`).then((r) => r.data),
  taxonomy: () => api.get("/kb/taxonomy").then((r) => r.data),
  companyProfile: () => api.get("/kb/company-profile").then((r) => r.data),
  saveProfileBlock: (block, data) =>
    api.put("/kb/company-profile", { block, data }).then((r) => r.data),
  importPreview: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/kb/import", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  importConfirm: (payload) => api.post("/kb/import/confirm", payload).then((r) => r.data),
};
